import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { bookingService } from '../../_services/booking.service';
import { marketplaceService } from '../../_services/marketplace.service';
import { addBookingToCalendar } from '../../_utils/calendarEvent';
import toast from '../shared/Toast';
import BookingManageFlows from './BookingManageFlows';
import { seriesFor, differenceSaid } from './seriesHelpers';
import ReviewModal from './ReviewModal';
import {
  startOf, clientStartOf, clientMinutesOf, daysFromToday, countdownLabel, listStatusOf, isCancelled, headlineOf, isImg,
  rescheduleDeadline, blockedRescheduleCopy, fmtShort,
} from './bookingHelpers';
import {
  WhenBlock, ServicesBlock, WhoRow, WhereBlock, ChangesBlock, ActionBar, deadlineCaption,
  IconBack, IconShare,
} from './bookingSections';
import './BookingDetailsPage.css';

/* One booking, in reading order: the salon photo with Back and Share floating
   over it, when, what you booked, who, where (map last), the policy, and the
   actions locked in a bar at the foot of the screen. Route: /dashboard/bookings/:id */

// loc arrives as [lng, lat] or GeoJSON { coordinates: [lng, lat] }
const coordsFromLoc = (loc) => {
  const c = Array.isArray(loc) ? loc : (loc && Array.isArray(loc.coordinates) ? loc.coordinates : null);
  if (!c || c.length < 2) return null;
  const lng = Number(c[0]); const lat = Number(c[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};
const staffAvatarOf = (m) => {
  if (!m) return '';
  const ui = m.userImage || {};
  return ui.circleImage || ui.squareImage || m.avatar || m.image || '';
};
const bookingAvatarOf = (b) => {
  const im = (b && b.hairdresserImage) || {};
  return typeof im === 'string' ? im : (im.circleImage || im.rectangleImage || '');
};

/* ---------- Data ---------- */

// The booking itself: v2 client endpoint, flattened from bookings[0].
const useBooking = (bookingId, token, refreshKey) => {
  const [state, setState] = useState({ booking: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    if (!bookingId) { setState({ booking: null, loading: false, error: 'No booking ID provided' }); return undefined; }
    setState((s) => ({ ...s, loading: true, error: null }));
    bookingService.getClientBookingById(bookingId, token).then((response) => {
      if (cancelled) return;
      const envErr = response.data && response.data.error;
      if ((response.data && response.data.errorCode) || (envErr && envErr.errorCode) || (response.data && response.data.statusCode === 0)) {
        setState({ booking: null, loading: false, error: (envErr && envErr.responseMessage) || (response.data && response.data.messages) || 'Failed to load booking details' });
        return;
      }
      let data = (response.data && (response.data.responseData || response.data.data)) || response.data;
      if (data && Array.isArray(data.bookings) && data.bookings.length) data = { ...data, ...data.bookings[0] };
      setState({ booking: data, loading: false, error: null });
    }).catch(() => { if (!cancelled) setState({ booking: null, loading: false, error: 'Failed to load booking details' }); });
    return () => { cancelled = true; };
  }, [bookingId, token, refreshKey]);
  return state;
};

// Place and person from the public summaries. The booking carries only the
// stylist id, so: staff summary (avatar, role, the salon's id, address and
// pin), then the salon summary (headline photo, slug, contact routes).
const usePlace = (hairdresserId) => {
  const [place, setPlace] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!hairdresserId) { setReady(true); return undefined; }
    setReady(false);
    (async () => {
      try {
        const res = await marketplaceService.getStaffSummary({ hairdresserId });
        if (cancelled || !res) return;
        let salon = res.salon || null;
        const member = res.staff || (res.team || []).find((m) => m._id === hairdresserId) || null;
        if (salon && (salon._id || salon.id)) {
          try {
            const more = await marketplaceService.getSalonSummary({ salonId: salon._id || salon.id });
            if (cancelled) return;
            if (more && more.salon) salon = { ...more.salon, ...salon, galleryImages: more.salon.galleryImages, image: more.salon.image || salon.image, slug: more.salon.slug || salon.slug, onlineBookingContactPhone: more.salon.onlineBookingContactPhone, onlineBookingContactEmail: more.salon.onlineBookingContactEmail, phoneNumber: more.salon.phoneNumber || salon.phoneNumber };
          } catch (e) { /* contact routes and the headline stay hidden */ }
        }
        if (cancelled) return;
        setPlace({
          hero: headlineOf(salon),
          salonId: (salon && (salon._id || salon.id)) || '',
          slug: (salon && salon.slug) || '',
          salonName: (salon && (salon.name || salon.salonName)) || '',
          address: (salon && salon.formattedAddress) || '',
          coords: coordsFromLoc(salon && salon.loc),
          salonImage: (salon && salon.image) || '',
          phone: (salon && (salon.onlineBookingContactPhone || salon.phoneNumber || salon.phone)) || '',
          email: (salon && salon.onlineBookingContactEmail) || '',
          stylist: member ? { name: member.name || '', avatar: staffAvatarOf(member), role: member.jobTitle || (member.isSalonOwner ? 'Owner' : 'Stylist') } : null,
          // The stylist's no-show policy is on the public staff summary (the
          // fee itself is not), so the page can say that one applies.
          noShow: !!(member && Number(member.noShowStatus) === 1),
        });
      } catch (e) {
        /* the page renders from the booking alone */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hairdresserId]);
  return { place, ready };
};

// What the salon allows right now, from the same previews the flows use.
const usePolicy = (bookingId, live, refreshKey) => {
  const [policy, setPolicy] = useState({ loading: false, resched: null, cancel: null });
  useEffect(() => {
    if (!bookingId || !live) { setPolicy({ loading: false, resched: null, cancel: null }); return undefined; }
    let cancelled = false;
    setPolicy({ loading: true, resched: null, cancel: null });
    Promise.all([
      bookingService.previewReschedule({ bookingId }).then((r) => (r && r.data && r.data.responseData) || null).catch(() => null),
      bookingService.previewCancellationFee({ bookingId }).then((r) => (r && r.data && r.data.responseData) || null).catch(() => null),
    ]).then(([resched, cancel]) => { if (!cancelled) setPolicy({ loading: false, resched, cancel }); });
    return () => { cancelled = true; };
  }, [bookingId, live, refreshKey]);
  return policy;
};

// Top bar turns solid once the photo has scrolled away; the bottom bar only
// carries a shadow while there is content beneath it. Inside the sheet the
// scroller is the sheet itself and the photo moves at a slower pace than the
// content (parallax) via a CSS variable on the hero.
const useScrollState = (heroRef, scrollerRef, ready) => {
  const [solid, setSolid] = useState(false);
  const [floating, setFloating] = useState(true);
  useEffect(() => {
    const el = scrollerRef && scrollerRef.current;
    const target = el || window;
    const on = () => {
      const h = heroRef.current;
      const topEdge = el ? el.getBoundingClientRect().top : 0;
      // No hero yet (the skeleton) means the photo has not scrolled away:
      // keep the bar transparent, otherwise it turns solid over the skeleton
      // and then fades off the top of the photo when the content arrives.
      const bottom = h ? h.getBoundingClientRect().bottom - topEdge : Infinity;
      setSolid(bottom <= 56);
      if (el) {
        setFloating(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
        if (h) h.style.setProperty('--bd-py', `${Math.round(Math.max(0, el.scrollTop) * 0.4)}px`);
      } else {
        setFloating(window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 4);
      }
    };
    on();
    target.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { target.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, [heroRef, scrollerRef, ready]);
  return { solid, floating };
};

/* ---------- Pieces ---------- */

// Inside the sheet the bar is sticky at the top of the sheet's own scroller;
// as a page it is fixed to the viewport.
const TopBar = ({ solid, title, onBack, onShare, inline }) => {
  const bar = (
    <div className={`bd-topbar${solid ? ' is-solid' : ''}${inline ? ' bd-topbar--sticky' : ''}`}>
      <div className="bd-topbar-inner">
        <button type="button" className="bd-disc" onClick={onBack} aria-label="Back to my bookings"><IconBack /></button>
        <span className="bd-topbar-title" aria-hidden={!solid}>{title}</span>
        <button type="button" className="bd-disc" onClick={onShare} aria-label="Share this booking"><IconShare /></button>
      </div>
    </div>
  );
  return inline ? bar : createPortal(bar, document.body);
};

const Skeleton = ({ inSheet, topbar }) => (
  <div className={`bd-page${inSheet ? ' bd-page--sheet' : ''}`} aria-busy="true" aria-label="Loading your booking">
    {topbar}
    <div className="bd-hero bd-hero--skel" />
    <div className="bd-body">
      <div className="bs-when"><div className="bs-skel" style={{ width: '70%', height: 26 }} /><div className="bs-skel" style={{ width: '40%', height: 18, marginTop: 10 }} /><div className="bs-skel" style={{ width: 150, height: 36, marginTop: 12, borderRadius: 999 }} /></div>
      <div className="bs-section"><div className="bs-skel" style={{ width: 130, height: 18 }} />{[1, 2].map((i) => <div key={i} className="bs-skel" style={{ height: 15, marginTop: 14 }} />)}<div className="bs-skel" style={{ height: 15, width: '50%', marginTop: 14 }} /></div>
      <div className="bs-section"><div className="bs-skel" style={{ width: 50, height: 18 }} /><div className="bd-skel-row"><div className="bs-skel" style={{ width: 44, height: 44, borderRadius: '50%' }} /><div><div className="bs-skel" style={{ width: 140, height: 14 }} /><div className="bs-skel" style={{ width: 90, height: 12, marginTop: 8 }} /></div></div></div>
      <div className="bs-section"><div className="bs-skel" style={{ width: 60, height: 18 }} /><div className="bd-skel-row"><div className="bs-skel" style={{ width: 44, height: 44, borderRadius: 10 }} /><div><div className="bs-skel" style={{ width: 160, height: 14 }} /><div className="bs-skel" style={{ width: 200, height: 12, marginTop: 8 }} /></div></div><div className="bs-skel" style={{ aspectRatio: '2 / 1', marginTop: 12, borderRadius: 12 }} /></div>
    </div>
  </div>
);

/* ---------- Page ---------- */

const BookingDetailsPage = ({ bookingId: bookingIdProp, inSheet = false, scrollerRef = null, onClose = null }) => {
  const params = useParams();
  const bookingId = bookingIdProp || params.id;
  const location = useLocation();
  const history = useHistory();
  const heroRef = useRef(null);

  const token = (() => {
    try { return new URLSearchParams(location.search).get('token') || sessionStorage.getItem(`guestManageToken:${bookingId}`) || localStorage.getItem(`guestManageToken:${bookingId}`) || null; } catch (e) { return null; }
  })();
  const [refreshKey, setRefreshKey] = useState(0);
  const [manage, setManage] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  const { booking, loading, error } = useBooking(bookingId, token, refreshKey);
  const hid = (booking && (booking.hairdresserId || (booking.hairdresserInfo && booking.hairdresserInfo._id))) || '';
  const { place, ready: placeReady } = usePlace(hid);

  const start = booking ? clientStartOf(booking) : null;
  const cancelled = isCancelled(booking);
  const finished = !!booking && (booking.bookingStatus === 4 || booking.bookingStatus === 6 || booking.status === 'completed');
  const upcomingLive = !!booking && !cancelled && !finished && startOf(booking).getTime() > Date.now();
  const isPast = !!booking && !cancelled && !upcomingLive && (finished || daysFromToday(startOf(booking)) < 0);
  const pending = !!booking && (booking.bookingStatus === 0 || booking.status === 'pending');
  const policy = usePolicy(booking && booking._id, upcomingLive, refreshKey);
  const { solid, floating } = useScrollState(heroRef, scrollerRef, !loading && placeReady);

  // Pin the page to the top on arrival: the list's scroll lock hands back the
  // list's old offset after the route has changed.
  useEffect(() => {
    if (booking && !error) {
      try {
        if (scrollerRef && scrollerRef.current) scrollerRef.current.scrollTop = 0;
        else window.scrollTo(0, 0);
      } catch (e) { /* ignore */ }
    }
  }, [booking, error, scrollerRef]);

  const goBack = useCallback(() => { if (onClose) onClose(); else history.push('/dashboard/bookings'); }, [history, onClose]);
  const pageClass = `bd-page${inSheet ? ' bd-page--sheet' : ''}`;

  if (loading || (!placeReady && !error)) {
    const tb = <TopBar solid={false} title="Your booking" onBack={goBack} onShare={() => {}} inline={inSheet} />;
    return inSheet ? <Skeleton inSheet topbar={tb} /> : <>{tb}<Skeleton /></>;
  }

  if (error || !booking) {
    const tb = <TopBar solid title="Your booking" onBack={goBack} onShare={() => {}} inline={inSheet} />;
    return (
      <>
        {!inSheet && tb}
        <div className={`${pageClass} bd-page--error`}>
          {inSheet && tb}
          <div className="bd-body">
            <h1 className="bs-date">Couldn't open this booking</h1>
            <p className="bd-error-text">{error || 'It may have been removed, or the link is out of date.'}</p>
            <button type="button" className="bs-btn bs-btn--primary" onClick={goBack}>My bookings</button>
          </div>
        </div>
      </>
    );
  }

  /* ----- Derived ----- */
  const services = booking.services || booking.servicesBooked || [];
  // Whole visit (incl. processing + finishing), so the end time and the
  // calendar event are right for colour bookings too.
  const totalMinutes = clientMinutesOf(booking);
  const total = Number(booking.totalAmount) || services.reduce((s, x) => s + (parseFloat(x.servicePrice) || 0), 0);
  const symbol = booking.currencySymbol || '£';
  const deposit = booking.isDepositPay ? Number(booking.depositAmount) || 0 : 0;
  const salonName = (place && place.salonName) || booking.salonName || '';
  const address = (place && place.address) || booking.salonAddress || '';
  const stylistName = (place && place.stylist && place.stylist.name) || booking.hairdresserName || '';
  const stylistAvatar = (place && place.stylist && place.stylist.avatar) || bookingAvatarOf(booking);
  const stylistRole = (place && place.stylist && place.stylist.role) || 'Stylist';
  const bookingSalonImage = typeof booking.salonImage === 'string' ? booking.salonImage : ((booking.salonImage && booking.salonImage.url) || '');
  const hero = (place && place.hero) || (isImg(bookingSalonImage) ? bookingSalonImage : '');
  const salonImage = (place && place.salonImage) || bookingSalonImage;
  const slug = (place && place.slug) || '';
  const sid = (place && place.salonId) || booking.salonId || '';
  const stylistUrl = !hid ? '' : slug ? `/book/${slug}/professional/${hid}` : `/book/pro/${hid}`;
  const salonUrl = slug ? `/book/${slug}` : sid ? `/salon/${sid}` : '';
  const serviceIds = services.map((x) => x.serviceId).filter(Boolean);
  const rebookUrl = stylistUrl ? `${stylistUrl}${serviceIds.length ? `?rebookServiceIds=${serviceIds.join(',')}` : ''}` : '';

  const status = listStatusOf(booking, { past: isPast });
  const chip = upcomingLive && !pending ? [countdownLabel(start), status && status.text].filter(Boolean).join(' · ') : (pending ? 'Waiting for the salon' : (status && status.text) || '');

  const rp = policy.resched;
  const by = rp && rp.canReschedule ? rescheduleDeadline(booking, rp) : null;
  const cp = policy.cancel;
  const lines = [];
  if (rp) lines.push({ kind: 'move', ok: !!rp.canReschedule, text: rp.canReschedule ? (by ? `Free to move until ${fmtShort(by)}.` : 'Free to move online.') : blockedRescheduleCopy(rp.reason) });
  if (cp) lines.push({ kind: 'cancel', ok: !cp.willBeCharged, text: cp.willBeCharged ? `Cancelling now costs ${cp.currencySymbol || symbol}${Number(cp.cancellationFee || 0).toFixed(2)} under the salon's cancellation policy.` : 'No fee if you cancel now.' });
  if (place && place.noShow && !policy.loading) lines.push({ kind: 'noshow', ok: false, text: cp && cp.hasPaymentMethod ? 'Missing the appointment may be charged to your saved card under the salon\'s no-show policy.' : 'Missing the appointment may be charged under the salon\'s no-show policy.' });
  const cardSaved = !!(booking.isCardCapture && cp && cp.hasPaymentMethod);

  const manageBooking = {
    ...booking,
    hairdresserInfo: booking.hairdresserInfo || { _id: hid, name: stylistName },
    salonInfo: booking.salonInfo || { _id: sid, salonName, phone: place && place.phone },
  };
  const startManage = (type) => setManage({ type, booking: manageBooking, nonce: Date.now() });

  /* ----- Actions ----- */
  const addToCalendar = () => {
    const end = new Date(start.getTime() + (totalMinutes || 60) * 60000);
    const serviceList = services.map((s) => s.serviceName).filter(Boolean).join(', ') || 'Appointment';
    const url = `${window.location.origin}/dashboard/bookings/${booking._id}`;
    return addBookingToCalendar({
      uid: `booking-${booking._id}@beu.app`, sequence: 0, start, end,
      title: `${serviceList} at ${salonName || 'the salon'}`,
      description: [`With ${stylistName || 'your stylist'}`, `Booking ID ${booking._id}`, `Manage it at ${url}`].join('\n'),
      location: [salonName, address].filter(Boolean).join(', '), url,
      filename: `beu-booking-${String(booking._id).slice(-8)}`,
    });
  };
  const share = async () => {
    const data = { title: 'My booking', text: `I've booked ${services[0] ? services[0].serviceName : 'an appointment'} at ${salonName || 'the salon'} on ${fmtShort(start)}`, url: window.location.href };
    if (navigator.share) { try { await navigator.share(data); } catch (e) { /* dismissed */ } return; }
    try { await navigator.clipboard.writeText(`${data.text} ${data.url}`); toast.success('Link copied'); } catch (e) { /* ignore */ }
  };
  const copyId = () => navigator.clipboard.writeText(String(booking._id));

  /* ----- Bar ----- */
  let bar = null;
  if (upcomingLive) {
    bar = {
      caption: pending ? 'Waiting for the salon to accept this booking' : deadlineCaption(by),
      secondary: { label: 'Cancel booking', onClick: () => startManage('cancel') },
      primary: pending ? null : { label: 'Reschedule', onClick: () => startManage('reschedule') },
    };
  } else if (isPast) {
    // The single-booking endpoint has no review flags; the list passes what it
    // knows through location state when it opens the sheet.
    const canReview = booking.canReview === true || !!(location.state && location.state.canReview);
    bar = {
      secondary: canReview ? { label: 'Leave a review', onClick: () => setReviewOpen(true) } : null,
      primary: rebookUrl ? { label: 'Book again', onClick: () => history.push(rebookUrl) } : null,
    };
  }

  const topbar = <TopBar solid={solid} title="Your booking" onBack={goBack} onShare={share} inline={inSheet} />;
  const actionBar = bar ? <ActionBar caption={bar.caption} secondary={bar.secondary} primary={bar.primary} floating={floating} /> : null;
  const announce = (type) => { try { window.dispatchEvent(new CustomEvent('beu:bookings-changed', { detail: { id: booking._id, type } })); } catch (e) { /* ignore */ } };
  const onCancelled = (id, type) => { setRefreshKey((k) => k + 1); announce(type || 'cancelled'); };
  const onReviewed = () => { setReviewOpen(false); setRefreshKey((k) => k + 1); announce('reviewed'); };

  return (
    <>
      {!inSheet && topbar}
      <div className={`${pageClass}${bar ? ' has-bar' : ''}`}>
        {inSheet && topbar}
        <div className={`bd-hero${hero ? '' : ' bd-hero--empty'}`} ref={heroRef}>
          {hero && <img className={`bd-hero-img${heroLoaded ? ' is-loaded' : ''}`} src={hero} alt="" onLoad={() => setHeroLoaded(true)} />}
          {chip && <span className={`bd-chip${heroLoaded || !hero ? ' is-in' : ''}`}>{chip}</span>}
        </div>

        <div className="bd-body">
          <div className="bd-rise" style={{ '--i': 0 }}>
            <WhenBlock start={start} minutes={totalMinutes} onAddToCalendar={cancelled ? null : addToCalendar} />
            {/* Part of a repeat: say so, and say it loudly when this visit is
                different from the usual one */}
            {!cancelled && booking.seriesId && (() => {
              const s = seriesFor(booking._id);
              return (
                <>
                  <p className="bd-repeat">Part of a repeat booking{s && s.said ? `: ${s.said.charAt(0).toLowerCase()}${s.said.slice(1)}` : ''}.</p>
                  {s && s.diffs.length > 0 && <p className="bd-repeat-diff" role="note">{differenceSaid(s.diffs)}.</p>}
                </>
              );
            })()}
          </div>
          {services.length > 0 && (
            <div className="bd-rise" style={{ '--i': 1 }}>
              <ServicesBlock services={services} symbol={symbol} total={total} deposit={deposit} cardSaved={cardSaved} tax={booking.tax} />
            </div>
          )}
          <div className="bd-rise" style={{ '--i': 2 }}>
            <WhoRow name={stylistName} avatar={stylistAvatar} role={stylistRole} onGo={stylistUrl ? () => history.push(stylistUrl) : null} />
          </div>
          <div className="bd-rise" style={{ '--i': 3 }}>
            <WhereBlock salonName={salonName} address={address} image={salonImage} coords={place && place.coords} phone={place && place.phone} email={place && place.email} onGo={salonUrl ? () => history.push(salonUrl) : null} />
          </div>
          <div className="bd-rise" style={{ '--i': 4 }}>
            <ChangesBlock loading={upcomingLive && policy.loading} lines={lines} bookingId={booking._id} onCopy={copyId} />
          </div>
        </div>
        {inSheet && actionBar}
      </div>

      {!inSheet && actionBar && createPortal(actionBar, document.body)}

      <BookingManageFlows request={manage} onClose={() => setManage(null)} onCancelled={onCancelled} history={history} />
      <ReviewModal isOpen={reviewOpen} booking={manageBooking} onClose={() => setReviewOpen(false)} onReviewSubmitted={onReviewed} />
    </>
  );
};

export default BookingDetailsPage;
